import { useState, useRef, useEffect } from 'react';

interface NotepadBarProps {
  prompt: string;
  value: string;
  onChange: (value: string) => void;
  accentColor?: string;
  readOnly?: boolean;
}

export function NotepadBar({ prompt, value, onChange, accentColor, readOnly = false }: NotepadBarProps) {
  const [isExpanded, setIsExpanded] = useState(!value); // Auto-expand if empty
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when expanded and empty
  useEffect(() => {
    if (isExpanded && !value && textareaRef.current && !readOnly) {
      textareaRef.current.focus();
    }
  }, [isExpanded, value, readOnly]);

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 transition-all duration-300 ease-in-out bg-white dark:bg-slate-800"
      style={{ '--color-accent': accentColor } as React.CSSProperties}
    >
      {/* Main bar - constrained width like slide content */}
      <div className="max-w-4xl mx-auto border-t-2 border-slate-200 dark:border-slate-700 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        {/* Header - always visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between px-6 py-3 hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors"
        >
          <div className="flex items-center gap-3">
            {/* Pencil icon */}
            <div className="w-7 h-7 rounded-lg accent-bg flex items-center justify-center flex-shrink-0">
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
              </svg>
            </div>

            <div className="text-left">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {prompt}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Preview of answer when collapsed */}
            {!isExpanded && value && (
              <span className="text-sm text-slate-500 dark:text-slate-400 max-w-[300px] truncate italic">
                "{value}"
              </span>
            )}

            {/* Expand/collapse icon */}
            <svg
              className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
          </div>
        </button>

        {/* Expanded content */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-6 pb-4">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              readOnly={readOnly}
              placeholder="Start typing..."
              className={`
                w-full p-3 rounded-lg border border-slate-200 dark:border-slate-600
                bg-slate-50 dark:bg-slate-900/50
                text-slate-700 dark:text-slate-300
                placeholder-slate-400 dark:placeholder-slate-500
                text-sm leading-relaxed resize-none
                focus:outline-none focus:ring-2 focus:ring-offset-0
                transition-colors
                ${readOnly ? 'cursor-default opacity-80' : ''}
              `}
              style={{ '--tw-ring-color': accentColor } as React.CSSProperties}
              rows={3}
            />
            {!readOnly && (
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1.5">
                Saved automatically • Stays with this part
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
